import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';

function toDate(v) {
  if (!v) return new Date(0);
  return v?.toDate ? v.toDate() : new Date(v);
}

/**
 * Visits/interactions use server timestamps; session_start_time on live_status is often the artist's
 * client clock. If we filter by time BEFORE checking session_id, matching rows can be wrongly excluded
 * during live sessions (zeros until the session ends). Prefer explicit session_id, with slack before start.
 */
const SESSION_FUNNEL_START_SLACK_MS = 24 * 60 * 60 * 1000;

/** Include interactions written before session_id was known (null/empty) for the same time window. */
function interactionInSessionWindow(i, sessionId, start, end) {
  const t = toDate(i.timestamp);
  const startMs = start.getTime();
  const endMs = end.getTime();

  if (i.session_id === sessionId) {
    return t.getTime() <= endMs && t.getTime() >= startMs - SESSION_FUNNEL_START_SLACK_MS;
  }

  if (t < start || t > end) return false;
  if (i.session_id == null || i.session_id === '') {
    if (String(sessionId).startsWith('karaoke_')) return false;
    return true;
  }
  return false;
}

/**
 * Song interactions for a host + live session.
 * Must include rows where session_id was still null (race before live_status propagated).
 * Prefers artist_id + date queries (UTC date on docs) to avoid huge downloads.
 */
export async function getSongInteractionsForSession(artistId, sessionId, sessionStart, sessionEnd) {
  if (!artistId || !sessionId) return [];
  const start = toDate(sessionStart);
  const end = toDate(sessionEnd);
  const d1 = start.toISOString().split('T')[0];
  const d2 = end.toISOString().split('T')[0];
  const dates = d1 === d2 ? [d1] : [d1, d2];

  let docs = [];
  try {
    for (const dateStr of dates) {
      const snap = await getDocs(
        query(
          collection(db, 'song_interactions'),
          where('artist_id', '==', artistId),
          where('date', '==', dateStr)
        )
      );
      docs.push(...snap.docs);
    }
  } catch (e) {
    console.warn('song_interactions date-scoped query failed, using artist_id fallback:', e?.message || e);
    const wide = await getDocs(query(collection(db, 'song_interactions'), where('artist_id', '==', artistId)));
    docs = wide.docs;
  }

  const rows = docs.map((d) => d.data());
  return rows.filter((i) => interactionInSessionWindow(i, sessionId, start, end));
}

/**
 * Admin live funnel / session history: count page visits for this session.
 * Includes legacy visits without session_id if they fall in the session time window.
 */
export function pageVisitBelongsToSessionFunnel(visit, sessionId, start, end) {
  const visitTime = visit.timestamp?.toDate ? visit.timestamp.toDate() : new Date(visit.timestamp || 0);
  const startMs = start.getTime();
  const endMs = end.getTime();
  const vt = visitTime.getTime();
  if (!vt || Number.isNaN(vt)) return false;

  if (visit.session_id === sessionId) {
    return vt <= endMs && vt >= startMs - SESSION_FUNNEL_START_SLACK_MS;
  }

  if (visitTime < start || visitTime > end) return false;
  if (!visit.session_id) {
    if (String(sessionId).startsWith('karaoke_')) return false;
    return true;
  }
  return false;
}

/**
 * Funnel visits for one session. Prefer rows tagged with this session_id;
 * keep untagged (legacy) visits only when that visitor has no session-tagged row,
 * so trackPageView + recordLiveSessionPageVisit do not double-count.
 */
export function visitsForSessionFunnel(visits, sessionId, start, end) {
  const belonging = (visits || []).filter((visit) =>
    pageVisitBelongsToSessionFunnel(visit, sessionId, start, end)
  );
  const tagged = belonging.filter((v) => v.session_id === sessionId);
  const taggedVisitors = new Set(tagged.map((v) => v.visitor_id).filter(Boolean));
  const orphans = belonging.filter(
    (v) => !v.session_id && (!v.visitor_id || !taggedVisitors.has(v.visitor_id))
  );
  return [...tagged, ...orphans];
}

export function filterInteractionsForSession(rows, sessionId, start, end) {
  return (rows || []).filter((i) => interactionInSessionWindow(i, sessionId, start, end));
}

/**
 * Karaoke requests scoped to one live session: match session_id, or legacy rows with no session_id
 * but created within the session time window (single live session per host).
 */
export async function getKaraokeRequestsForSessionWindow(hostId, sessionId, sessionStart, sessionEnd) {
  if (!hostId || !sessionId) return [];
  const start = toDate(sessionStart);
  const end = toDate(sessionEnd);

  const wide = await getDocs(
    query(collection(db, 'karaoke_requests'), where('karaoke_host_id', '==', hostId))
  );

  return wide.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => {
      if (r.session_id === sessionId) return true;
      if (r.session_id) return false;
      const t = toDate(r.created_at);
      return t >= start && t <= end;
    });
}

/**
 * Artist-mode `requests` for one live session (session_id set on each doc).
 */
export async function getArtistRequestsForSession(artistId, sessionId) {
  if (!artistId || !sessionId) return [];
  try {
    const snap = await getDocs(
      query(
        collection(db, 'requests'),
        where('artist_id', '==', artistId),
        where('session_id', '==', sessionId)
      )
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('requests session query failed, using artist_id fallback:', e?.message || e);
    const wide = await getDocs(query(collection(db, 'requests'), where('artist_id', '==', artistId)));
    return wide.docs.map((d) => ({ id: d.id, ...d.data() })).filter((r) => r.session_id === sessionId);
  }
}

export function mergeRequestSnapshotsById(...lists) {
  const map = new Map();
  lists.flat().forEach((r) => {
    const id = r?.id || r?.firestoreId;
    if (!id) return;
    map.set(id, { ...(map.get(id) || {}), ...r, id });
  });
  return Array.from(map.values());
}

/**
 * A2B / PayPal finalise historically wrote paid karaoke skip payments into `requests`
 * (artist_id = host uid, session_id = karaoke session). Merge those rows into karaoke analytics/history.
 */
export async function getLegacyGatewayKaraokeRowsFromRequests(hostId, sessionId) {
  if (!hostId || !sessionId || !String(sessionId).startsWith('karaoke_')) return [];
  let docs = [];
  try {
    const snap = await getDocs(
      query(
        collection(db, 'requests'),
        where('artist_id', '==', hostId),
        where('session_id', '==', sessionId)
      )
    );
    docs = snap.docs;
  } catch {
    const wide = await getDocs(query(collection(db, 'requests'), where('artist_id', '==', hostId)));
    docs = wide.docs.filter((d) => d.data().session_id === sessionId);
  }
  return docs.map((d) => {
    const r = d.data();
    return {
      id: d.id,
      karaoke_host_id: r.artist_id,
      session_id: r.session_id,
      song_title: r.song_title || '',
      artist_name: r.original_artist || r.artist_name || '',
      requester_name: r.requester_name || '',
      note: r.note || '',
      status: r.status || 'pending',
      skip_queue: true,
      amount_paid: r.amount_paid,
      total_paid: r.total_paid,
      payment_provider: r.payment_provider,
      transaction_reference: r.transaction_reference,
      payment_intent_id: r.payment_intent_id,
      order_reference: r.order_reference,
      gateway_xref: r.gateway_xref,
      currency: r.currency,
      _synced_from_requests_collection: true,
    };
  });
}
